import { ApolloError } from '@apollo/client';

import { useInactivateSchoolMembersMutation, UserSchoolMembershipFilter } from 'shared/graphql';
import { useSchoolConfiguration, useToast } from 'shared/hooks';
import { recordError } from 'shared/utils/record';

const INACTIVATING_MEMBERS_MESSAGE_KEY = 'INACTIVATING_MEMBERS_MESSAGE';

type Options = Parameters<typeof useInactivateSchoolMembersMutation>[0];

/**
 * Custom hook to inactivate school memberships into a school.
 *
 * This hook provides a function to inactivate school memberships by their user keys and
 * handles the success and error notifications accordingly.
 *
 * @returns An object containing:
 * - `mutate`: A function to inactivate school memberships.
 * - `loading`: A boolean indicating if the mutation is in progress.
 *
 * @example
 * const { mutate, loading } = useInactivateMembers();
 *
 * const handleInactivateMembers = async () => {
 *   await mutate({ id: { equals: 'user1' } });
 * };
 */
export function useInactivateMembers(options?: Options) {
  const { showMessage, showSuccess, showError, dismiss } = useToast();
  const { data: school } = useSchoolConfiguration();

  const [mutation, { loading }] = useInactivateSchoolMembersMutation(options);

  const mutate = async (filter: UserSchoolMembershipFilter) => {
    if (!school) {
      return;
    }

    try {
      showMessage(`Requesting inactivation of selected member(s).`, {
        id: INACTIVATING_MEMBERS_MESSAGE_KEY,
      });

      await mutation({
        variables: {
          filter,
          school: { id: school.id },
        },
      });

      showSuccess(
        'Inactivation request submitted successfully. The process may take some time, and the user(s) will be notified upon completion.',
      );
    } catch (err) {
      recordError(err);

      if (err instanceof ApolloError) {
        const [issue, ...issues] = err.graphQLErrors ?? [];

        let message = issue.message;

        if (issues.length) {
          message += ` (${issues.length} issues more)`;
        }

        showError(message, { reportable: false });
        return;
      }

      if (err instanceof Error) {
        showError(err.message);
      }
    } finally {
      dismiss(INACTIVATING_MEMBERS_MESSAGE_KEY);
    }
  };

  return {
    mutate,
    loading,
  };
}
